import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authenticatedFetch } from '../../../lib/api';
import { useToast } from '../../../components/shared/Toast';
import { erpPageBtnPrimaryClass, erpPageBtnSecondaryClass } from '@euroasiann/shared/ui';
import { pmFieldClass, pmLabelClass, pmTextareaClass } from '../port-management.ui';

interface WarehouseFormProps {
    initialData?: any;
    onSuccess: () => void;
    onCancel: () => void;
}

export function WarehouseForm({ initialData, onSuccess, onCancel }: WarehouseFormProps) {
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
        defaultValues: initialData ? {
            ...initialData,
            portId: initialData.portId?._id || initialData.portId,
        } : {
            name: '',
            address: '',
            portId: '',
            distanceFromPort: '',
            contactPerson: '',
            phone: '',
            email: '',
            status: 'Active',
        },
    });
    const toast = useToast();

    // Fetch ports for dropdown
    const { data: ports = [] } = useQuery({
        queryKey: ['ports-dropdown'],
        queryFn: async () => {
            const res = await authenticatedFetch('/api/v1/ports?status=Active&limit=1000');
            const data = await res.json();
            return data.data || [];
        },
    });

    const mutation = useMutation({
        mutationFn: async (data: any) => {
            const url = initialData ? `/api/v1/warehouses/${initialData._id}` : '/api/v1/warehouses';
            const method = initialData ? 'PUT' : 'POST';

            const res = await authenticatedFetch(url, {
                method,
                body: JSON.stringify(data),
                headers: { 'Content-Type': 'application/json' },
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to save warehouse');
            }
            return res.json();
        },
        onSuccess: () => {
            toast.success(initialData ? 'Warehouse updated' : 'Warehouse created');
            onSuccess();
        },
        onError: (err: any) => {
            toast.error(err.message);
        },
    });

    const onSubmit = (data: any) => {
        mutation.mutate(data);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Warehouse Name *</label>
                    <input
                        {...register('name', { required: 'Name is required' })}
                        className={pmFieldClass}
                    />
                    {errors.name && <span className="text-red-500 text-xs">{errors.name.message as string}</span>}
                </div>
                <div>
                    <label className={pmLabelClass}>Port *</label>
                    <select
                        {...register('portId', { required: 'Port is required' })}
                        className={pmFieldClass}
                    >
                        <option value="">Select Port</option>
                        {ports.map((port: any) => (
                            <option key={port._id} value={port._id}>
                                {port.name} ({port.code})
                            </option>
                        ))}
                    </select>
                    {errors.portId && <span className="text-red-500 text-xs">{errors.portId.message as string}</span>}
                </div>
            </div>

            <div>
                <label className={pmLabelClass}>Address *</label>
                <textarea
                    {...register('address', { required: 'Address is required' })}
                    className={pmTextareaClass}
                    rows={2}
                />
                {errors.address && <span className="text-red-500 text-xs">{errors.address.message as string}</span>}
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className={pmLabelClass}>Distance from Port (km)</label>
                    <input
                        type="number"
                        step="any"
                        {...register('distanceFromPort')}
                        className={pmFieldClass}
                    />
                </div>
                <div>
                    <label className={pmLabelClass}>Status</label>
                    <select
                        {...register('status')}
                        className={pmFieldClass}
                    >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <div>
                    <label className={pmLabelClass}>Contact Person</label>
                    <input
                        {...register('contactPerson')}
                        className={pmFieldClass}
                    />
                </div>
                <div>
                    <label className={pmLabelClass}>Phone</label>
                    <input
                        {...register('phone')}
                        className={pmFieldClass}
                    />
                </div>
                <div>
                    <label className={pmLabelClass}>Email</label>
                    <input
                        type="email"
                        {...register('email')}
                        className={pmFieldClass}
                    />
                </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={onCancel} className={erpPageBtnSecondaryClass}>
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`${erpPageBtnPrimaryClass} disabled:opacity-50`}
                >
                    {isSubmitting ? 'Saving...' : 'Save Warehouse'}
                </button>
            </div>
        </form>
    );
}
